import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Switch, Route, Link } from 'react-router-dom';
import { connect } from 'react-redux';

import {
  Button,
  COLOR,
  SideBarMenu,
  SideBarMenuContainer,
  SideBarMenuEntry,
  SideBarMenuGroup,
} from '@dized/ui';
import logo from '@dized/ui/assets/dized_logo.png';

import OrganizationActions from '../../actions/OrganizationActions';
import UserActions from '../../actions/UserActions';

import Games from '../Games';
import GameEditor from '../GameEditor';
import GameInfo from '../GameInfo';
import OrgAdmin from '../OrgAdmin';
import ProjectInfo from '../ProjectInfo';
import Analytics from '../Analytics';

import './Menu.css';

const Content = styled.div`
  min-width: 500px;
  padding: 20px;
`;

const Header = styled.div`
  color: ${COLOR.LIGHT_TURQUOISE};
  padding-bottom: 1em;
  text-align: center;
`;

const UserEmail = styled.div`
  color: ${COLOR.LIGHT_TURQUOISE};
  font-size: 0.85em;
  font-weight: 500;
  margin-bottom: 0.25em;
  word-break: break-word;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Custom styled Link component that matches SideBarMenuEntry styling
const MenuLink = styled(Link)`
  height: 1.75em;
  line-height: 1.75em;
  font-size: 0.8rem;
  color: ${COLOR.LIGHT_TURQUOISE};
  text-decoration: none;
  display: block;
  cursor: pointer;

  &:hover {
    color: ${COLOR.LIGHT_TURQUOISE};
    text-decoration: none;
  }

  &:visited {
    color: ${COLOR.LIGHT_TURQUOISE};
  }
`;

// Custom styled external link component that matches SideBarMenuEntry styling
const ExternalLink = styled.a`
  height: 1.75em;
  line-height: 1.75em;
  font-size: 0.8rem;
  color: ${COLOR.LIGHT_TURQUOISE};
  text-decoration: none;
  display: block;
  cursor: pointer;

  &:hover {
    color: ${COLOR.LIGHT_TURQUOISE};
    text-decoration: none;
  }

  &:visited {
    color: ${COLOR.LIGHT_TURQUOISE};
  }
`;

const mapStateToProps = (store) => ({
  invalidOrganization: store.db.organization.invalid,
  invalidSession: store.session.invalidSession,
  organization: store.db.organization,
  session: store.session.session,
});

const mapDispatchToProps = (dispatch) => ({
  ...OrganizationActions.bindActions(dispatch),
  ...UserActions.bindActions(dispatch),
});

class Menu extends Component {
  static propTypes = {
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    invalidOrganization: PropTypes.bool,
    invalidSession: PropTypes.bool,
    match: PropTypes.shape({
      params: PropTypes.shape({
        orgName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    organization: PropTypes.object,
    session: PropTypes.object,
    GET_ORGANIZATION: PropTypes.func.isRequired,
    LOGOUT: PropTypes.func.isRequired,
  };

  state = {
    errorMessage: '',
    errorTitle: '',
    modalOpen: false,
  };

  componentDidMount() {
    const { GET_ORGANIZATION } = this.props;
    const { orgName } = this.props.match.params;
    
    GET_ORGANIZATION(orgName);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.invalidSession !== this.props.invalidSession) {
      this.getOrganization();
    }
  }

  getOrganization = async () => {
    const { invalidSession, match: { params }, GET_ORGANIZATION } = this.props;
    const { orgName } = params;

    try {
      await GET_ORGANIZATION(orgName);
      if (this.props.invalidOrganization) throw new Error();
    } catch {
      if (orgName && !invalidSession) {
        this.setState({
          errorTitle: 'Invalid Organization',
          errorMessage: (
            <div>
              <p>
                You have entered an incorrect URL above, or you do not have
                permission to access this organization.
              </p>
              <p>
                The organization you entered is <strong>{orgName}</strong>{' '}
                which is invalid.
              </p>
              <p>
                <em>
                  Please enter the correct URL to continue using this
                  application or contact Dized support.
                </em>
              </p>
            </div>
          ),
        });
      }
    }
  };

  handleLogoutClick = async () => {
    const { LOGOUT } = this.props;
    await new Promise((resolve) =>
      this.setState(
        {
          errorTitle: '',
          errorMessage: '',
        },
        resolve,
      ),
    );
    
    await LOGOUT();
  };

  renderModal() {
    const { invalidSession } = this.props;
    const { errorMessage, errorTitle } = this.state;
    if (!errorMessage) return null;

    return (
      <div className="Menu-modal-background">
        <div className="Menu-modal">
          <h2 className="Menu-modal-title">{errorTitle}</h2>
          {errorMessage}
          {!invalidSession && (
            <div className="Menu-modal-button-container">
              <Button onClick={this.handleLogoutClick}>Logout</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const {
      history: { push },
      match: { params },
      organization: { name },
      session,
    } = this.props;
    const { orgName } = params;

    // Helper function to truncate long strings
    const truncateString = (str, maxLength = 25) => {
      if (!str || str.length <= maxLength) return str;
      return str.substring(0, maxLength - 3) + '...';
    };

    // Get user display information from session
    const getEmailDisplay = () => {
      if (!session) {
        return 'Not logged in';
      }
      
      // Primary: Use first name and last name if available
      if (session.firstName || session.lastName) {
        const name = [session.firstName, session.lastName].filter(Boolean).join(' ');
        return truncateString(name);
      }
      
      // Secondary: Use email if available
      if (session.email) {
        return truncateString(session.email);
      }
      
      return 'Email not available';
    };

    return (
      <SideBarMenuContainer>
        <SideBarMenu
          header={
            <Header onClick={() => push(`/${orgName}/games`)} style={{ cursor: 'pointer' }}>
              <img src={logo} alt="Dized" />
              <div>{name}</div>
            </Header>
          }
        >
          <SideBarMenuGroup title="CONTENT">
            <MenuLink to={`/${orgName}/games`}>
              All Titles
            </MenuLink>
            <MenuLink to={`/${orgName}/game/new-game/edit`}>
              Create New Title
            </MenuLink>
            <MenuLink to={`/${orgName}/analytics`}>
              Analytics
            </MenuLink>
          </SideBarMenuGroup>
          
          <SideBarMenuGroup title="MARKETING">
            <ExternalLink 
              href="https://dized.com/advertizing/" 
              target="_blank"
              rel="noopener noreferrer"
            >
              Advertizing on DIZED
            </ExternalLink>
            <ExternalLink 
              href="https://go.dzd.to/dized-ad-campaign-setup" 
              target="_blank"
              rel="noopener noreferrer"
            >
              Create Ad Campaign
            </ExternalLink>
          </SideBarMenuGroup>
          
          <SideBarMenuGroup title="DOCUMENTATION">
            <ExternalLink 
              href="https://create.dized.com/" 
              target="_blank"
              rel="noopener noreferrer"
            >
              DIZED Portal Guide
            </ExternalLink>
            <ExternalLink 
              href="https://portalguide.dized.com/tutorial-tool-overview/" 
              target="_blank"
              rel="noopener noreferrer"
            >
              Tutorial Guide
            </ExternalLink>
            <ExternalLink 
              href="https://portalguide.dized.com/rules-tool-overview/" 
              target="_blank"
              rel="noopener noreferrer"
            >
              Rules Guide
            </ExternalLink>
          </SideBarMenuGroup>
          
          <SideBarMenuGroup title="SUPPORT">
            <ExternalLink 
              href="https://discord.com/invite/bq8t3wg" 
              target="_blank"
              rel="noopener noreferrer"
            >
              Creator Discord
            </ExternalLink>
            <ExternalLink 
              href="mailto:b2b@dized.com"
            >
              Email
            </ExternalLink>
          </SideBarMenuGroup>
          
          <SideBarMenuGroup title="ACCOUNT">
            <div className="account-info">
              <div className="account-label">
                Logged in as:
              </div>
              <UserEmail className="account-email">
                {getEmailDisplay()}
              </UserEmail>
            </div>
            <SideBarMenuEntry onClick={this.handleLogoutClick} path="" style={{ cursor: 'pointer' }}>
              Logout
            </SideBarMenuEntry>
          </SideBarMenuGroup>
        </SideBarMenu>
        <Content>
          <Switch>
            <Route path="/:orgName/admin" component={OrgAdmin} />
            <Route path="/:orgName/analytics" component={Analytics} />
            <Route path="/:orgName/games" component={Games} />
            <Route path="/:orgName/game/:gameId/analytics" component={Analytics} />
            <Route path="/:orgName/game/:id/edit" component={GameEditor} />
            <Route path="/:orgName/game/:id" component={GameInfo} />
            <Route
              path="/:orgName/project/:gameId/:projectId/analytics"
              component={Analytics}
            />
            <Route
              path="/:orgName/project/:gameId/:projectId"
              component={ProjectInfo}
            />
            <Route path="/:orgName" component={Games} />
          </Switch>
          {this.renderModal()}
        </Content>
      </SideBarMenuContainer>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Menu);